import React, { useState, useCallback, useEffect } from 'react';
import { useTheme } from './ThemeContext';
import { submitContactForm } from '../../services/contactApi';
import { 
  Mail, Phone, MapPin, ArrowRight, Send, MessageCircle, Clock, 
  Loader2, CheckCircle, AlertTriangle, Sparkles, Target, Compass, Globe
} from 'lucide-react';

export default function Contact({ isVisible = true }) {
  const { isDarkMode } = useTheme();
  
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', subject: '', message: ''
  });
  
  const [selectedCountry, setSelectedCountry] = useState('');
  const [customCountry, setCustomCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // --- LIVE CLOCK LOGIC (IST) ---
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getOfficeStatus = () => {
    const options = { timeZone: 'Asia/Kolkata', hour12: false, hour: '2-digit', weekday: 'long' };
    const istInfo = new Intl.DateTimeFormat('en-US', options).formatToParts(new Date());
    const hour = parseInt(istInfo.find(p => p.type === 'hour').value);
    const day = istInfo.find(p => p.type === 'weekday').value;

    const isSunday = day === 'Sunday';
    const isOpen = isSunday ? (hour >= 10 && hour < 18) : (hour >= 9 && hour < 20);

    return {
      isOpen,
      text: isOpen ? 'Office Open' : 'Office Closed',
      color: isOpen ? 'text-green-500' : 'text-red-500',
      dot: isOpen ? 'bg-green-500' : 'bg-red-500'
    };
  };

  const officeStatus = getOfficeStatus();

  // --- FORM HANDLERS ---
  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleCountryChange = useCallback((e) => {
    const value = e.target.value;
    setSelectedCountry(value);
    if (value !== 'Other') setCustomCountry('');
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    const finalData = {
      ...formData,
      country: selectedCountry === 'Other' ? customCountry : selectedCountry
    };
    try {
      await submitContactForm(finalData);
      setSuccess(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
      setSelectedCountry(''); setCustomCountry('');
    } catch (err) {
      setError(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  const highlights = [
    { icon: Compass, title: "Expert Guidance", desc: "Across admissions, counselling, and coaching." },
    { icon: Globe, title: "Personalised Solutions", desc: "Tailored for global learning pathways." },
    { icon: Target, title: "Focused Mentorship", desc: "Goal-driven education support." }
  ];

  const inputClass = `w-full px-4 sm:px-5 py-3 sm:py-4 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all ${
    isDarkMode ? 'bg-gray-900/50 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-100'
  }`;

  if (success) {
    return (
      <section className={`py-24 px-4 transition-colors duration-500 ${isDarkMode ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-3xl mx-auto text-center animate-fade-in">
          <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h3 className={`text-4xl font-black mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Message Sent!</h3>
          <p className={`text-lg mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Our counselors will contact you within 24 hours.</p>
          <button onClick={() => setSuccess(false)} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-xl font-bold">Send Another</button>
        </div>
      </section>
    );
  }

  return (
    <section className={`relative py-16 sm:py-20 lg:py-24 px-4 overflow-hidden transition-colors duration-500 ${
      isDarkMode ? 'bg-black text-white' : 'bg-white text-gray-900'
    }`}>
      
      {/* Background Decor */}
      <div className={`absolute inset-0 ${isDarkMode ? 'opacity-10' : 'opacity-5'}`}>
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(${isDarkMode ? 'rgba(59,130,246,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? 'rgba(59,130,246,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px)`, backgroundSize: '50px 50px' }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* --- TOP HIGHLIGHTS --- */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          {highlights.map((h, i) => (
            <div key={i} className={`p-6 rounded-2xl border backdrop-blur-md flex gap-4 items-center transition-transform hover:-translate-y-1 ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center text-white shrink-0 shadow-lg shadow-blue-500/20">
                <h.icon size={24} />
              </div>
              <div>
                <h4 className="font-bold text-sm uppercase tracking-wider text-blue-500">Feature</h4>
                <p className="font-bold leading-tight">{h.title}</p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{h.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {/* LEFT: INFO & CLOCK */}
          <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div>
              <span className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-500 px-4 py-2 rounded-full text-xs font-bold border border-blue-500/20 mb-6">
                <Sparkles size={14} /> REALIZE YOUR DREAMS
              </span>
              <h2 className="text-4xl sm:text-6xl font-black mb-6 leading-tight">
                Let's Start Your <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Educational Journey</span>
              </h2>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 font-medium opacity-80"><div className="w-1.5 h-1.5 rounded-full bg-blue-500"/>Expert guidance across admissions and coaching</li>
                <li className="flex items-center gap-3 font-medium opacity-80"><div className="w-1.5 h-1.5 rounded-full bg-purple-500"/>Personalised solutions for global pathways</li>
                <li className="flex items-center gap-3 font-medium opacity-80"><div className="w-1.5 h-1.5 rounded-full bg-pink-500"/>Focused mentorship for goal-driven education</li>
              </ul>
            </div>

            {/* LIVE CLOCK CARD */}
            <div className={`p-6 rounded-3xl border flex items-center justify-between transition-all ${isDarkMode ? 'bg-blue-900/10 border-blue-500/20 shadow-blue-500/5' : 'bg-gray-50 border-gray-200 shadow-xl'}`}>
              <div className="flex items-center gap-5">
                <div className="relative w-16 h-16 rounded-full border-4 border-blue-500/20 flex items-center justify-center">
                  <Clock className="text-blue-500 animate-pulse" />
                  <div className="absolute inset-0 border-4 border-t-blue-500 rounded-full animate-spin-slow" />
                </div>
                <div>
                  <p className="text-2xl font-black">{currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                  <p className="text-xs font-bold opacity-50 uppercase tracking-widest">Local Time (IST)</p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end mb-1">
                  <span className={`w-2 h-2 rounded-full ${officeStatus.dot} animate-pulse`} />
                  <span className={`font-black text-xs uppercase ${officeStatus.color}`}>{officeStatus.text}</span>
                </div>
                <p className="text-[10px] opacity-60 font-medium">Mon-Sat: 9AM - 8PM</p>
              </div>
            </div>

            {/* CONTACT LINKS */}
            <div className="grid sm:grid-cols-2 gap-4">
               {[
                { icon: Mail, label: 'Email', value: 'info@vidyazon.com', href: 'mailto:info@vidyazon.com', color: 'from-blue-500 to-cyan-500' },
                { icon: Phone, label: 'Call', value: '+91 9997517305', href: 'tel:+919997517305', color: 'from-purple-500 to-pink-500' }
               ].map((c, i) => (
                 <a key={i} href={c.href} className={`flex items-center gap-4 p-4 rounded-2xl border transition-all hover:scale-105 ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${c.color} text-white flex items-center justify-center shrink-0`}><c.icon size={18}/></div>
                    <div className="min-w-0"><p className="text-[10px] font-bold opacity-50 uppercase">{c.label}</p><p className="font-bold truncate text-sm">{c.value}</p></div>
                 </a>
               ))}
            </div>
          </div>

          {/* RIGHT: FORM */}
          <div className={`backdrop-blur-xl rounded-3xl p-6 sm:p-10 border-t-4 shadow-2xl transition-all duration-1000 ${
              isDarkMode ? 'bg-gray-800/40 border-blue-500' : 'bg-white border-blue-600'
            } ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white"><Send size={20}/></div>
              <h3 className="text-2xl font-black">Send Us a Message</h3>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleFormChange} className={inputClass} required />
              <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleFormChange} className={inputClass} required />
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleFormChange} className={inputClass} required />
              <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleFormChange} className={inputClass} required />
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <select name="subject" value={formData.subject} onChange={handleFormChange} className={inputClass} required>
                <option value="">Subject</option>
                <option value="Admissions">Admissions</option>
                <option value="Visa">Visa Assistance</option>
                <option value="Scholarship">Scholarship</option>
              </select>
              <select value={selectedCountry} onChange={handleCountryChange} className={inputClass} required>
                <option value="">Preferred Study Country</option>
                {['INDIA', 'USA', 'UK', 'Canada', 'Australia', 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {selectedCountry === 'Other' && <input type="text" placeholder="Specify Country" value={customCountry} onChange={(e) => setCustomCountry(e.target.value)} className={`${inputClass} mb-4 animate-fade-in`} required />}

            <textarea name="message" placeholder="Your Message" value={formData.message} onChange={handleFormChange} rows="4" className={`${inputClass} mb-6 resize-none`} required />

            {error && <div className="flex items-center gap-3 p-3 mb-6 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl"><AlertTriangle size={18} /> <span className="text-xs font-bold">{error}</span></div>}

            <button type="button" onClick={handleSubmit} disabled={loading} className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-5 rounded-2xl font-black text-lg shadow-xl hover:shadow-blue-500/20 active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50">
              {loading ? <Loader2 className="animate-spin" /> : <><Send size={20} /> Submit Inquiry <ArrowRight size={20} /></>}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin-slow { animation: spin-slow 10s linear infinite; }
        .animate-fade-in { animation: fadeIn 0.5s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </section>
  );
}